import React, { useMemo, useRef } from "react";
import { CircularProgress, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Controller, Control } from "react-hook-form";
import { createListboxScrollHandler } from "../../utilities/infiniteScroll";

const LoadingListbox = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLElement> & { loading?: boolean; loadingMore?: boolean }
>(function LoadingListbox(props, ref) {
  const { children, loading, loadingMore, ...other } = props;
  const showLoadingSpinner = !!loading || !!loadingMore;

  return (
    <ul ref={ref} {...other}>
      {children}
      {showLoadingSpinner ? (
        <li
          aria-hidden
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "8px 0",
            pointerEvents: "none",
            listStyle: "none",
          }}
        >
          <CircularProgress size={20} />
        </li>
      ) : null}
    </ul>
  );
});

type Props = {
  name: string;
  label: string;
  control: Control<any>;
  options: string[];
  formatLabel?: (value: string) => string;
  required?: boolean;
  disabled?: boolean;
  freeSolo?: boolean;
  onSearchChange?: (query: string) => void;
  onListOpen?: () => void;
  onLoadMore?: () => void;
  loading?: boolean;
  loadingMore?: boolean;
};

export const TemporaryDrugFormSelect: React.FC<Props> = ({
  name,
  label,
  control,
  options,
  formatLabel = (value) => value,
  required = false,
  disabled = false,
  freeSolo = false,
  onSearchChange,
  onListOpen,
  onLoadMore,
  loading = false,
  loadingMore = false,
}) => {
  const serverFiltered = !!onSearchChange;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const listboxProps = useMemo(
    () =>
      onLoadMore
        ? {
            loading,
            loadingMore,
            onScroll: createListboxScrollHandler(() => onLoadMoreRef.current?.()),
          }
        : undefined,
    [onLoadMore, loading, loadingMore],
  );

  return (
  <Controller
    name={name}
    control={control}
    rules={required ? { required: `${label} is required` } : undefined}
    render={({ field, fieldState }) => (
        <Autocomplete
          freeSolo={freeSolo}
          fullWidth
          disabled={disabled}
          openOnFocus
          loading={loading}
          options={options}
          value={freeSolo ? field.value ?? "" : field.value ?? null}
          onChange={(_, newValue) => {
            const next = freeSolo
              ? typeof newValue === "string"
                ? newValue
                : newValue ?? ""
              : newValue ?? "";

            field.onChange(next);
            if (serverFiltered) {
              onSearchChange?.(next);
            }
          }}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "input" || reason === "clear") {
              if (freeSolo) {
                field.onChange(newInputValue);
              }
              onSearchChange?.(newInputValue);
            }
          }}
          onOpen={() => onListOpen?.()}
          filterOptions={serverFiltered ? (opts) => opts : undefined}
          getOptionLabel={(option) => formatLabel(option)}
          getOptionSelected={(option, value) => option === value}
          ListboxComponent={onLoadMore ? LoadingListbox : undefined}
          ListboxProps={listboxProps}
          renderInput={(params) => (
      <TextField
              {...params}
        label={label}
        fullWidth
        required={required}
              placeholder="Search..."
        error={!!fieldState.error}
        helperText={fieldState.error?.message}
        InputLabelProps={{ shrink: true }}
            />
          )}
        />
    )}
  />
);
};
